import { useState } from "react"
import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import { 
  Plus,
  Search,
  Filter,
  Eye,
  MessageSquare,
  FileText,
  Bot,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  PoundSterling,
  Zap,
  X,
  Settings,
  Loader2,
} from "lucide-react"
import { useDisputeColumnPreferences } from "@/hooks/useDisputeColumnPreferences"
import { DisputeColumnConfigDialog } from "@/components/disputes/column-config-dialog"
import { useDisputesList, useCreateDispute, useUpdateDispute, useCustomerOptions, useDisputeTypeOptions, useUserOptions } from "@/hooks/useDisputesData"
import type { DisputeUI } from "@/types/ui/disputes"
import type { CreateDisputeRequest, UpdateDisputeRequest } from "@/types/api/disputes"
import { FeatureFeedbackCollector } from "@/components/FeedbackCollector"

const statusColors = {
  "New": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "Under Review": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Escalated": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "Resolved": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "Closed": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
}

const priorityColors = {
  "Low": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  "Medium": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "High": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Critical": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
}

const communications = [
  {
    date: "2024-01-16 10:30 AM",
    user: "John Smith",
    type: "Note",
    message: "Contacted customer to verify service dates. Awaiting response.",
  },
  {
    date: "2024-01-15 2:15 PM",
    user: "AI Agent",
    type: "Automated",
    message: "Dispute automatically categorized as 'Billing Error' with 85% confidence.",
  },
  {
    date: "2024-01-15 9:00 AM",
    user: "System",
    type: "Status Change",
    message: "Dispute status changed from 'New' to 'In Progress'",
  },
]

const documents = [
  { name: "Original Invoice.pdf", size: "245 KB", date: "2024-01-15" },
  { name: "Service Agreement.pdf", size: "1.2 MB", date: "2024-01-15" },
  { name: "Customer Email.msg", size: "45 KB", date: "2024-01-16" },
]

const initialCreateFormState = {
  customerId: "",
  disputeTypeId: "",
  amount: "",
  statusId: "1",
  priorityId: "",
  assignedToUserId: "",
  dueDate: "",
  description: "",
}

export default function DisputesPage() {
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [priorityFilter, setPriorityFilter] = useState("all")
  const [selectedDispute, setSelectedDispute] = useState<DisputeUI | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [selectedDisputeId, setSelectedDisputeId] = useState<string | null>(null)
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false)
  const [createForm, setCreateForm] = useState(initialCreateFormState)

  const { data: disputes = [], isLoading, refetch: refetchDisputes } = useDisputesList()
  const createDisputeMutation = useCreateDispute()
  const { data: customerOptions = [] } = useCustomerOptions()
  const { data: disputeTypeOptions = [] } = useDisputeTypeOptions()
  const { data: userOptions = [] } = useUserOptions()
  
  const selectedDisputeNumericId = selectedDispute ? parseInt(selectedDispute.id.replace("DSP-", "")) : 0
  const updateDisputeMutation = useUpdateDispute(selectedDisputeNumericId)

  const [editForm, setEditForm] = useState({
    statusId: "",
    priorityId: "",
    assignedToUserId: "",
    dueDate: "",
    description: "",
    resolutionNotes: "",
  })

  const handleCreateFormChange = (field: string, value: string) => {
    setCreateForm(prev => ({ ...prev, [field]: value }))
  }

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  const handleUpdateDispute = async () => {
    if (!selectedDispute) return

    const request: UpdateDisputeRequest = {}
    if (editForm.statusId) request.statusId = parseInt(editForm.statusId)
    if (editForm.priorityId) request.priorityId = parseInt(editForm.priorityId)
    if (editForm.assignedToUserId) request.assignedToUserId = parseInt(editForm.assignedToUserId)
    if (editForm.dueDate) request.dueDate = editForm.dueDate
    if (editForm.description) request.description = editForm.description
    if (editForm.resolutionNotes) request.resolutionNotes = editForm.resolutionNotes

    try {
      await updateDisputeMutation.mutateAsync(request)
      setIsViewDialogOpen(false)
      setEditForm({
        statusId: "",
        priorityId: "",
        assignedToUserId: "",
        dueDate: "",
        description: "",
        resolutionNotes: "",
      })
      refetchDisputes()
    } catch (error) {
      console.error("Failed to update dispute:", error)
    }
  }

  const handleCreateDispute = async () => {
    if (!createForm.customerId || !createForm.disputeTypeId || !createForm.amount || !createForm.priorityId) {
      return
    }

    const request: CreateDisputeRequest = {
      customerId: parseInt(createForm.customerId),
      disputeTypeId: parseInt(createForm.disputeTypeId),
      amount: parseFloat(createForm.amount),
      statusId: parseInt(createForm.statusId),
      priorityId: parseInt(createForm.priorityId),
      assignedToUserId: createForm.assignedToUserId ? parseInt(createForm.assignedToUserId) : undefined,
      dueDate: createForm.dueDate || undefined,
      description: createForm.description || undefined,
    }

    try {
      await createDisputeMutation.mutateAsync(request)
      setIsCreateDrawerOpen(false)
      setCreateForm(initialCreateFormState)
      refetchDisputes()
    } catch (error) {
      console.error("Failed to create dispute:", error)
    }
  }

  const {
    columns,
    visibleColumns,
    isSaving: isSavingColumns,
    savePreferences,
    resetToDefaults,
  } = useDisputeColumnPreferences()

  const renderColumnHeader = (columnId: string) => {
    const headerMap: Record<string, string> = {
      disputeId: "Dispute ID",
      customer: "Customer",
      type: "Type",
      amount: "Amount",
      status: "Status",
      priority: "Priority",
      workflowProgress: "Workflow Progress",
      aiScore: "AI Score",
      assignedTo: "Assigned To",
      dueDate: "Due Date",
      createdDate: "Created Date",
      workflow: "Workflow",
      description: "Description",
    }
    return headerMap[columnId] || columnId
  }

  const renderColumnCell = (columnId: string, dispute: DisputeUI) => {
    switch (columnId) {
      case "disputeId":
        return (
          <button
            onClick={() => handleViewDispute(dispute)}
            className="font-medium text-primary hover:underline"
          >
            {dispute.id}
          </button>
        )
      case "customer":
        return dispute.customer
      case "type":
        return dispute.type
      case "amount":
        return `£${dispute.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
      case "status":
        return (
          <Badge className={statusColors[dispute.status as keyof typeof statusColors]}>
            {dispute.status}
          </Badge>
        )
      case "priority":
        return (
          <Badge className={priorityColors[dispute.priority as keyof typeof priorityColors]}>
            {dispute.priority}
          </Badge>
        )
      case "workflowProgress":
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
              <div
                className="bg-primary h-2 rounded-full"
                style={{ width: `${(dispute.currentLevel / dispute.totalLevels) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {dispute.currentLevel}/{dispute.totalLevels}
            </span>
          </div>
        )
      case "aiScore":
        return dispute.aiScore !== null ? (
          <div className="flex items-center gap-1">
            <Bot className="h-3 w-3 text-purple-500" />
            <span className="text-sm font-medium">{dispute.aiScore}%</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      case "assignedTo":
        return dispute.assignedTo
      case "dueDate":
        return dispute.dueDate ? format(new Date(dispute.dueDate), "dd/MM/yyyy") : "—"
      case "createdDate":
        return format(new Date(dispute.createdDate), "MMM dd, yyyy")
      case "workflow":
        return dispute.workflow
      case "description":
        return (
          <span className="truncate max-w-[200px] block" title={dispute.description}>
            {dispute.description}
          </span>
        )
      default:
        return null
    }
  }

  const filteredDisputes = disputes.filter((dispute) => {
    const matchesSearch = 
      dispute.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dispute.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dispute.type.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = statusFilter === "all" || dispute.status === statusFilter
    const matchesPriority = priorityFilter === "all" || dispute.priority === priorityFilter

    return matchesSearch && matchesStatus && matchesPriority
  })

  const handleViewDispute = (dispute: DisputeUI) => {
    setSelectedDispute(dispute)
    setIsViewDialogOpen(true)
  }

  const totalDisputes = disputes.length
  const activeDisputes = disputes.filter(d => 
    d.status === "In Progress" || d.status === "Under Review"
  ).length
  const totalAmount = disputes.reduce((sum, d) => sum + d.amount, 0)
  const automationRate = 87

  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dispute Management</h1>
          <p className="text-muted-foreground">
            Track, manage, and resolve customer disputes with AI-powered automation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FeatureFeedbackCollector feature="Disputes" />
          <Button onClick={() => setIsCreateDrawerOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Dispute
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Disputes */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Disputes</CardTitle>
            <Activity className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDisputes}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingDown className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">-12%</span>
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Active Disputes */}
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Disputes</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeDisputes}</div>
            <p className="text-xs text-muted-foreground mt-1">
              2 new this week
            </p>
          </CardContent>
        </Card>

        {/* Disputed Amount */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disputed Amount</CardTitle>
            <PoundSterling className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{totalAmount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all active disputes
            </p>
          </CardContent>
        </Card>

        {/* AI Automation */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Automation</CardTitle>
            <Zap className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{automationRate}%</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingUp className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">+5%</span>
              <span className="ml-1">improvement</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Disputes</CardTitle>
              <CardDescription>
                Comprehensive view of all customer disputes
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search disputes..."
                  className="pl-8 w-[250px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Escalated">Escalated</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>

              {/* Priority Filter */}
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[150px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>

              {/* Column Configuration */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsColumnConfigOpen(true)}
                title="Configure columns"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                {visibleColumns.map((col) => (
                  <TableHead key={col.id}>{renderColumnHeader(col.id)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="h-64 text-center">
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Loading disputes...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredDisputes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="h-64 text-center">
                    <p className="text-sm text-muted-foreground">No disputes found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDisputes.map((dispute) => (
                  <TableRow 
                    key={dispute.id}
                    className={selectedDisputeId === dispute.id ? "bg-muted/50" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedDisputeId === dispute.id}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDisputeId(dispute.id)
                            setSelectedDispute(dispute)
                          } else {
                            setSelectedDisputeId(null)
                            setSelectedDispute(null)
                          }
                        }}
                        aria-label={`Select dispute ${dispute.id}`}
                      />
                    </TableCell>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.id}>{renderColumnCell(col.id, dispute)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Column Configuration Dialog */}
      <DisputeColumnConfigDialog
        open={isColumnConfigOpen}
        onOpenChange={setIsColumnConfigOpen}
        columns={columns}
        onColumnsChange={savePreferences}
        onResetToDefaults={resetToDefaults}
        isSaving={isSavingColumns}
      />

      {/* View Dispute Sheet */}
      <Sheet open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto px-6">
          {selectedDispute && (
            <>
              <SheetHeader className="px-0">
                <SheetTitle>Dispute Details - {selectedDispute.id}</SheetTitle>
                <SheetDescription>
                  View and manage dispute information
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="workflow">Workflow</TabsTrigger>
                  <TabsTrigger value="communications">Communications</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
                </TabsList>

                {/* Tab 1: Overview */}
                <TabsContent value="overview" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Customer</Label>
                      <Input value={selectedDispute.customer} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Dispute Type</Label>
                      <Input value={selectedDispute.type} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input value={`£${selectedDispute.amount.toLocaleString()}`} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Input value={selectedDispute.status} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Input value={selectedDispute.priority} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Assigned To</Label>
                      <Input value={selectedDispute.assignedTo} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Created Date</Label>
                      <Input value={format(new Date(selectedDispute.createdDate), "MMM dd, yyyy")} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Input value={format(new Date(selectedDispute.dueDate), "MMM dd, yyyy")} readOnly />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={selectedDispute.description} readOnly rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label>Resolution Notes</Label>
                    <Textarea 
                      placeholder="Add resolution notes..." 
                      rows={4}
                      value={editForm.resolutionNotes || selectedDispute.resolutionNotes}
                      onChange={(e) => handleEditFormChange("resolutionNotes", e.target.value)}
                    />
                  </div>
                </TabsContent>

                {/* Tab 2: Workflow */}
                <TabsContent value="workflow" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Active Workflow</CardTitle>
                      <CardDescription>{selectedDispute.workflow}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-sm font-medium">Current Level:</span>
                        <Badge>{selectedDispute.currentLevel} of {selectedDispute.totalLevels}</Badge>
                      </div>

                      <div className="space-y-6">
                        {[
                          {
                            level: 1,
                            title: "Initial Review & Categorization",
                            description: "Dispute received and automatically categorized by AI",
                            completedDate: "2024-01-15",
                          },
                          {
                            level: 2,
                            title: "Investigation & Evidence Gathering",
                            description: "Collecting relevant documents and customer communications",
                            completedDate: selectedDispute.currentLevel >= 2 ? "2024-01-16" : null,
                          },
                          {
                            level: 3,
                            title: "Resolution Recommendation",
                            description: "AI generates resolution recommendation based on analysis",
                            completedDate: selectedDispute.currentLevel >= 3 ? "2024-01-17" : null,
                          },
                          {
                            level: 4,
                            title: "Final Approval & Closure",
                            description: "Management approval and customer notification",
                            completedDate: selectedDispute.currentLevel >= 4 ? "2024-01-18" : null,
                          },
                        ].map((step) => (
                          <div key={step.level} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                step.level <= selectedDispute.currentLevel
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground"
                              }`}>
                                {step.level < selectedDispute.currentLevel ? (
                                  <CheckCircle2 className="h-4 w-4" />
                                ) : step.level === selectedDispute.currentLevel ? (
                                  <Clock className="h-4 w-4" />
                                ) : (
                                  <span className="text-sm">{step.level}</span>
                                )}
                              </div>
                              {step.level < 4 && (
                                <div className={`w-0.5 h-12 ${
                                  step.level < selectedDispute.currentLevel
                                    ? "bg-primary"
                                    : "bg-muted"
                                }`} />
                              )}
                            </div>
                            <div className="flex-1 pb-8">
                              <h4 className="font-semibold">{step.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {step.description}
                              </p>
                              {step.completedDate && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  Completed: {format(new Date(step.completedDate), "MMM dd, yyyy")}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab 3: Communications */}
                <TabsContent value="communications" className="space-y-4">
                  <Button className="w-full">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Add Note
                  </Button>
                  <div className="space-y-4">
                    {communications.map((comm, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{comm.user}</span>
                              <Badge variant="outline" className="text-xs">
                                {comm.type}
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground">{comm.date}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{comm.message}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Tab 4: Documents */}
                <TabsContent value="documents" className="space-y-4">
                  <Button className="w-full">
                    <FileText className="mr-2 h-4 w-4" />
                    Upload Document
                  </Button>
                  <div className="space-y-3">
                    {documents.map((doc, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.size} • Uploaded {format(new Date(doc.date), "MMM dd, yyyy")}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Tab 5: AI Insights */}
                <TabsContent value="ai-insights" className="space-y-4">
                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        <CardTitle className="text-lg">AI-Powered Insights</CardTitle>
                      </div>
                      <CardDescription>
                        Automated analysis by Dispute Resolute Agent
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Confidence Score */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Confidence Score</Label>
                          <span className="text-sm font-medium">{selectedDispute.aiScore}%</span>
                        </div>
                        <Progress value={selectedDispute.aiScore} className="h-2" />
                      </div>

                      {/* AI Recommendation */}
                      <div className="space-y-2">
                        <Label>AI Recommendation</Label>
                        <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
                          <CardContent className="pt-4">
                            <p className="text-sm">{selectedDispute.aiRecommendation}</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Similar Cases */}
                      <div className="space-y-2">
                        <Label>Similar Cases Analysis</Label>
                        <p className="text-sm text-muted-foreground">
                          Based on analysis of 47 similar disputes, 82% were resolved with partial credit.
                          Average resolution time: 12 days.
                        </p>
                      </div>

                      {/* Risk Assessment */}
                      <div className="space-y-2">
                        <Label>Risk Assessment</Label>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            Medium Risk
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            Customer has good payment history
                          </span>
                        </div>
                      </div>

                      {/* Suggested Actions */}
                      <div className="space-y-2">
                        <Label>Suggested Actions</Label>
                        <ul className="mt-2 space-y-2">
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Verify service delivery dates from system logs</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Review contract terms for billing cycle</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Consider 30% credit as resolution</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <SheetFooter className="mt-6 px-0">
                <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                  Close
                </Button>
                <Button 
                  onClick={handleUpdateDispute}
                  disabled={updateDisputeMutation.isPending}
                >
                  {updateDisputeMutation.isPending ? "Updating..." : "Update Dispute"}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Create Dispute Drawer */}
      <Sheet open={isCreateDrawerOpen} onOpenChange={setIsCreateDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto px-6">
          <SheetHeader className="px-0">
            <SheetTitle>Create New Dispute</SheetTitle>
            <SheetDescription>
              Enter dispute details to create a new case
            </SheetDescription>
          </SheetHeader>

          <Tabs defaultValue="basic-info" className="mt-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic-info">Basic Info</TabsTrigger>
              <TabsTrigger value="workflow">Workflow</TabsTrigger>
              <TabsTrigger value="communications">Communications</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
            </TabsList>

            {/* Tab 1: Basic Info */}
            <TabsContent value="basic-info" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="customer">Customer</Label>
                  <Select value={createForm.customerId} onValueChange={(value) => handleCreateFormChange("customerId", value)}>
                    <SelectTrigger id="customer">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customerOptions.map((customer) => (
                        <SelectItem key={customer.id} value={String(customer.id)}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Dispute Type</Label>
                  <Select value={createForm.disputeTypeId} onValueChange={(value) => handleCreateFormChange("disputeTypeId", value)}>
                    <SelectTrigger id="type">
                      <SelectValue placeholder="Select dispute type" />
                    </SelectTrigger>
                    <SelectContent>
                      {disputeTypeOptions.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Disputed Amount</Label>
                  <Input 
                    id="amount" 
                    type="number" 
                    placeholder="0.00"
                    value={createForm.amount}
                    onChange={(e) => handleCreateFormChange("amount", e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={createForm.priorityId} onValueChange={(value) => handleCreateFormChange("priorityId", value)}>
                    <SelectTrigger id="priority">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Critical</SelectItem>
                      <SelectItem value="2">High</SelectItem>
                      <SelectItem value="3">Medium</SelectItem>
                      <SelectItem value="4">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="assigned-to">Assigned To</Label>
                  <Select value={createForm.assignedToUserId} onValueChange={(value) => handleCreateFormChange("assignedToUserId", value)}>
                    <SelectTrigger id="assigned-to">
                      <SelectValue placeholder="Select user (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {userOptions.map((user) => (
                        <SelectItem key={user.id} value={String(user.id)}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="due-date">Due Date</Label>
                  <Input 
                    id="due-date" 
                    type="date"
                    value={createForm.dueDate}
                    onChange={(e) => handleCreateFormChange("dueDate", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the dispute in detail..."
                  rows={6}
                  value={createForm.description}
                  onChange={(e) => handleCreateFormChange("description", e.target.value)}
                />
              </div>

              <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <Bot className="h-5 w-5 text-purple-500 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">AI-Powered Processing</p>
                      <p className="text-xs text-muted-foreground">
                        This dispute will be automatically categorized, prioritized, and routed to the
                        appropriate workflow by the Dispute Resolute Agent.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 2: Workflow */}
            <TabsContent value="workflow" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Select Workflow</CardTitle>
                  <CardDescription>Choose the appropriate workflow for this dispute</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="workflow-select">Workflow Template</Label>
                    <Select defaultValue="standard">
                      <SelectTrigger id="workflow-select">
                        <SelectValue placeholder="Select workflow" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard Billing Dispute</SelectItem>
                        <SelectItem value="escalated">Escalated Quality Dispute</SelectItem>
                        <SelectItem value="payment">Standard Payment Dispute</SelectItem>
                        <SelectItem value="quick">Quick Resolution</SelectItem>
                        <SelectItem value="legal">Legal Review Required</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-4 mt-6">
                    <p className="text-sm font-medium">Workflow Stages (4 levels)</p>
                    <div className="space-y-6">
                      {[
                        {
                          level: 1,
                          title: "Initial Review & Categorization",
                          description: "Dispute received and automatically categorized by AI",
                        },
                        {
                          level: 2,
                          title: "Investigation & Evidence Gathering",
                          description: "Collecting relevant documents and customer communications",
                        },
                        {
                          level: 3,
                          title: "Resolution Recommendation",
                          description: "AI generates resolution recommendation based on analysis",
                        },
                        {
                          level: 4,
                          title: "Final Approval & Closure",
                          description: "Management approval and customer notification",
                        },
                      ].map((step) => (
                        <div key={step.level} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                              <span className="text-sm">{step.level}</span>
                            </div>
                            {step.level < 4 && (
                              <div className="w-0.5 h-12 bg-muted" />
                            )}
                          </div>
                          <div className="flex-1 pb-8">
                            <h4 className="font-semibold">{step.title}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 3: Communications */}
            <TabsContent value="communications" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Initial Communication</CardTitle>
                  <CardDescription>Add any initial notes or communications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="initial-note">Initial Note</Label>
                    <Textarea
                      id="initial-note"
                      placeholder="Add any relevant notes about this dispute..."
                      rows={6}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="notify-customer" />
                    <Label htmlFor="notify-customer" className="text-sm font-normal cursor-pointer">
                      Send notification to customer about dispute creation
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox id="notify-assignee" />
                    <Label htmlFor="notify-assignee" className="text-sm font-normal cursor-pointer">
                      Notify assigned team member
                    </Label>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-muted/50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Communication History</p>
                      <p className="text-xs text-muted-foreground">
                        Communication history will be available after the dispute is created.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 4: Documents */}
            <TabsContent value="documents" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Supporting Documents</CardTitle>
                  <CardDescription>Upload any relevant documents for this dispute</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm font-medium mb-1">Drop files here or click to upload</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max 10MB)
                    </p>
                    <Button variant="outline" size="sm">
                      <FileText className="mr-2 h-4 w-4" />
                      Browse Files
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Document Types</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-invoice" />
                        <Label htmlFor="doc-invoice" className="text-sm font-normal cursor-pointer">
                          Invoice
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-contract" />
                        <Label htmlFor="doc-contract" className="text-sm font-normal cursor-pointer">
                          Contract
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-email" />
                        <Label htmlFor="doc-email" className="text-sm font-normal cursor-pointer">
                          Email Correspondence
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox id="doc-other" />
                        <Label htmlFor="doc-other" className="text-sm font-normal cursor-pointer">
                          Other
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 5: AI Insights */}
            <TabsContent value="ai-insights" className="space-y-4">
              <Card className="border-purple-200 dark:border-purple-800">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    <CardTitle className="text-lg">AI-Powered Analysis</CardTitle>
                  </div>
                  <CardDescription>
                    Preview of AI capabilities for dispute resolution
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Automated Categorization</Label>
                    <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
                      <CardContent className="pt-4">
                        <p className="text-sm">
                          The AI agent will automatically analyse the dispute description and categorise it
                          based on historical patterns and similar cases.
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-2">
                    <Label>Priority Assessment</Label>
                    <p className="text-sm text-muted-foreground">
                      AI will evaluate the dispute amount, customer history, and urgency to recommend
                      an appropriate priority level.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Similar Cases</Label>
                    <p className="text-sm text-muted-foreground">
                      Once created, the system will analyse similar historical disputes to provide
                      resolution recommendations and estimated timelines.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Workflow Routing</Label>
                    <p className="text-sm text-muted-foreground">
                      The dispute will be automatically routed to the most appropriate workflow based
                      on its characteristics and complexity.
                    </p>
                  </div>

                  <Card className="bg-muted/50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">AI Analysis Available After Creation</p>
                          <p className="text-xs text-muted-foreground">
                            Detailed AI insights and recommendations will be generated once the dispute
                            is created and processed by the system.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <SheetFooter className="mt-6 px-0">
            <Button variant="outline" onClick={() => setIsCreateDrawerOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateDispute}
              disabled={createDisputeMutation.isPending || !createForm.customerId || !createForm.disputeTypeId || !createForm.amount || !createForm.priorityId}
            >
              {createDisputeMutation.isPending ? "Creating..." : "Create Dispute"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Floating Actions Panel */}
      {selectedDisputeId && selectedDispute && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5">
          <Card className="shadow-2xl border-2 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Selected Dispute Info */}
                <div className="flex items-center gap-3 pr-4 border-r">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{selectedDispute.id}</p>
                    <p className="text-xs text-muted-foreground">{selectedDispute.customer}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDispute(selectedDispute)}
                    className="gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View Details
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Add Comment
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    View Documents
                  </Button>
                </div>

                {/* Close Button */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedDisputeId(null)
                    setSelectedDispute(null)
                  }}
                  className="ml-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
